// frontend/src/components/dashboard/DashboardSummary.jsx
import React, { useState, useEffect } from 'react';
import { 
  CurrencyRupeeIcon, 
  CalendarIcon, 
  MapPinIcon, 
  UserGroupIcon,
  FireIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ArrowPathIcon,
  HomeIcon,
  TruckIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  UsersIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';
import { toast } from 'react-toastify';
import Loader from '../common/Loader';

const DashboardSummary = ({ groupId, summary, membership, user, onTabChange, onViewProfile, navigate }) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalExpenses: 0,
    totalActivities: 0,
    totalRoutePlans: 0,
    membersCount: 0,
    upcomingActivities: [],
    recentExpenses: [],
    members: [],
    topRoutePlan: null,
    totalPolls: 0,
    totalChecklists: 0,
    totalAccommodations: 0,
    totalTransportations: 0
  });
  
  // Stats card data
  const [stats, setStats] = useState([
    { 
      id: 'expenses', 
      label: 'Total Expenses', 
      value: 0, 
      icon: CurrencyRupeeIcon,
      color: 'from-blue-500 to-blue-600',
      trend: '+12%',
      subValue: '₹0 per person'
    },
    { 
      id: 'activities', 
      label: 'Activities', 
      value: 0, 
      icon: CalendarIcon,
      color: 'from-green-500 to-emerald-600',
      trend: '+5',
      subValue: '0 upcoming'
    },
    { 
      id: 'routes', 
      label: 'Route Plans', 
      value: 0, 
      icon: MapPinIcon,
      color: 'from-purple-500 to-pink-600',
      trend: '3 votes',
      subValue: 'Most popular'
    },
    { 
      id: 'members', 
      label: 'Members', 
      value: 0, 
      icon: UserGroupIcon,
      color: 'from-orange-500 to-red-600',
      trend: 'Active',
      subValue: 'You are member'
    }
  ]);

  // Quick actions - Removed invite option
  const quickActions = [
    { id: 'expenses', label: 'Add Expense', icon: CurrencyRupeeIcon, color: 'blue', tab: 'expenses' },
    { id: 'routes', label: 'Plan Route', icon: MapPinIcon, color: 'green', tab: 'routes' },
    { id: 'activities', label: 'Schedule', icon: CalendarIcon, color: 'purple', tab: 'activities' },
    { id: 'polls', label: 'Create Poll', icon: ChatBubbleLeftRightIcon, color: 'yellow', tab: 'polls' },
    { id: 'accommodation', label: 'Book Stay', icon: HomeIcon, color: 'indigo', tab: 'accommodation' },
    { id: 'transport', label: 'Transport', icon: TruckIcon, color: 'red', tab: 'transport' },
    { id: 'checklists', label: 'Checklist', icon: ClipboardDocumentCheckIcon, color: 'teal', tab: 'checklists' }
  ];
  
  useEffect(() => {
    if (summary) {
      updateDashboardData(summary);
    } else {
      loadDashboardData();
    }
  }, [groupId, summary]);

  const updateDashboardData = (summaryData) => {
    console.log('📊 Updating dashboard data:', summaryData);
    
    const totalExpenses = summaryData.totalExpenses || 0;
    const membersCount = summaryData.membersCount || 1;
    const perPerson = membersCount > 0 ? Math.round(totalExpenses / membersCount) : 0;
    
    const updatedStats = [...stats];
    updatedStats[0].value = totalExpenses;
    updatedStats[0].subValue = `₹${perPerson} per person`;
    updatedStats[1].value = summaryData.totalActivities || 0;
    updatedStats[1].subValue = `${summaryData.upcomingActivities?.length || 0} upcoming`;
    updatedStats[2].value = summaryData.totalRoutePlans || 0;
    updatedStats[2].subValue = summaryData.topRoutePlan?.totalVotes ? 
      `${summaryData.topRoutePlan.totalVotes} votes` : 'No votes yet';
    updatedStats[3].value = membersCount;
    updatedStats[3].subValue = membership?.isCreator ? 'You are leader' : 'You are member';
    
    setStats(updatedStats);
    
    // Process members to ensure they have proper names
    const processedMembers = (summaryData.members || []).map((member, index) => {
      // If name is just 'Member', try to get a better name
      let memberName = member.name || 'Member';
      if (memberName === 'Member' && member.id) {
        // Try to extract from email or use index
        if (member.email) {
          const emailName = member.email.split('@')[0];
          memberName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
        } else {
          memberName = `Member ${index + 1}`;
        }
      }
      
      return {
        ...member,
        name: memberName,
        role: member.role || 'member',
        isActive: member.isActive !== undefined ? member.isActive : true
      };
    });
    
    setDashboardData({
      totalExpenses,
      totalActivities: summaryData.totalActivities || 0,
      totalRoutePlans: summaryData.totalRoutePlans || 0,
      membersCount,
      upcomingActivities: summaryData.upcomingActivities || [],
      recentExpenses: summaryData.recentExpenses || [],
      members: processedMembers,
      topRoutePlan: summaryData.topRoutePlan || null,
      totalPolls: summaryData.totalPolls || 0,
      totalChecklists: summaryData.totalChecklists || 0,
      totalAccommodations: summaryData.totalAccommodations || 0,
      totalTransportations: summaryData.totalTransportations || 0
    });
    
    console.log('✅ Processed members:', processedMembers.map(m => ({ 
      name: m.name, 
      role: m.role,
      id: m.id,
      _id: m._id,
      userId: m.userId 
    })));
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const summaryData = await groupDashboardService.getDashboardSummary(groupId);
      updateDashboardData(summaryData);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadDashboardData();
      toast.success('Dashboard refreshed');
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // FIXED: Handle profile view - now uses correct route
  const handleViewProfile = (userId) => {
    console.log('🔍 handleViewProfile called with userId:', userId);
    
    if (!userId) {
      console.error('❌ No user ID provided');
      toast.error('Cannot view profile - user ID is missing');
      return;
    }
    
    // Validate the ID format
    if (typeof userId !== 'string' || userId.length < 10) {
      console.error('❌ Invalid user ID format:', userId);
      toast.error('Invalid user profile ID');
      return;
    }
    
    // FIXED: Use the correct route that matches App.js
    const profileUrl = `/user/${userId}`;
    console.log('📍 Navigating to:', profileUrl);
    
    if (onViewProfile && typeof onViewProfile === 'function') {
      console.log('✅ Using onViewProfile callback');
      onViewProfile(userId);
    } else if (navigate && typeof navigate === 'function') {
      console.log('✅ Using navigate function');
      navigate(profileUrl);
    } else {
      console.log('✅ Using window location');
      window.location.href = profileUrl;
    }
  };

  const handleQuickAction = (action) => {
    if (onTabChange && action.tab) {
      onTabChange(action.tab);
    }
  };

  const handleViewAll = (tab) => {
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  // Calculate color classes for quick actions
  const getColorClass = (color, type = 'bg') => {
    const classes = {
      blue: type === 'bg' ? 'bg-blue-100 group-hover:bg-blue-200' : 'text-blue-600',
      green: type === 'bg' ? 'bg-green-100 group-hover:bg-green-200' : 'text-green-600',
      purple: type === 'bg' ? 'bg-purple-100 group-hover:bg-purple-200' : 'text-purple-600',
      yellow: type === 'bg' ? 'bg-yellow-100 group-hover:bg-yellow-200' : 'text-yellow-600',
      indigo: type === 'bg' ? 'bg-indigo-100 group-hover:bg-indigo-200' : 'text-indigo-600',
      red: type === 'bg' ? 'bg-red-100 group-hover:bg-red-200' : 'text-red-600',
      teal: type === 'bg' ? 'bg-teal-100 group-hover:bg-teal-200' : 'text-teal-600'
    };
    return classes[color] || classes.blue;
  };

  if (loading) {
    return (
      <div className="py-8">
        <Loader size="lg" text="Loading your travel dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Welcome back, {user?.name?.split(' ')[0] || 'Traveler'}!
            </h1>
            {membership?.isCreator && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-semibold rounded-full">
                <FireIcon className="h-3 w-3" />
                Creator
              </span>
            )}
          </div>
          <p className="text-gray-600 text-sm md:text-base">
            Manage your trip efficiently with real-time updates
          </p>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats.map((stat, index) => (
          <div 
            key={stat.id}
            className={`bg-gradient-to-br ${stat.color} text-white rounded-2xl p-5 shadow-lg transform transition-transform hover:scale-[1.02]`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm opacity-90 mb-1">{stat.label}</p>
                <p className="text-2xl md:text-3xl font-bold">{stat.value}</p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <stat.icon className="h-6 w-6 opacity-90" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-white/20">
              <p className="text-xs opacity-90">{stat.subValue}</p>
              <span className="text-xs font-medium px-2 py-1 bg-white/20 rounded-full">
                {stat.trend}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900">Quick Actions</h2>
            <p className="text-gray-600 text-sm">Jump to any section instantly</p>
          </div>
          <ArrowRightIcon className="h-5 w-5 text-gray-400" />
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 md:gap-4">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => handleQuickAction(action)}
              className="group flex flex-col items-center p-3 md:p-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200"
            >
              <div className={`p-3 rounded-full ${getColorClass(action.color, 'bg')} mb-2 md:mb-3`}>
                <action.icon className={`h-5 w-5 md:h-6 md:w-6 ${getColorClass(action.color, 'text')}`} />
              </div>
              <span className="text-xs md:text-sm font-medium text-gray-700 text-center">
                {action.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Activities & Expenses */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upcoming Activities */}
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">Upcoming Activities</h3>
              </div>
              {dashboardData.upcomingActivities.length > 0 && (
                <button
                  onClick={() => handleViewAll('activities')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all ({dashboardData.upcomingActivities.length})
                </button>
              )}
            </div>
            
            {dashboardData.upcomingActivities.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.upcomingActivities.slice(0, 3).map((activity, index) => (
                  <div 
                    key={activity._id || index} 
                    className="flex items-start p-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="p-3 bg-purple-100 rounded-lg mr-4">
                      <CalendarIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="font-semibold text-gray-900 truncate">
                          {activity.title || `Activity ${index + 1}`}
                        </h4>
                        {activity.cost > 0 && (
                          <span className="text-sm font-semibold text-gray-900">
                            ₹{activity.cost}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2 truncate">
                        {activity.description || 'No description'}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {activity.date ? new Date(activity.date).toLocaleDateString() : 'Date TBD'}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          activity.category === 'sightseeing' ? 'bg-blue-100 text-blue-800' :
                          activity.category === 'adventure' ? 'bg-red-100 text-red-800' :
                          activity.category === 'food' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {activity.category || 'General'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-700 mb-1">No activities planned</h4>
                <p className="text-gray-500 text-sm mb-4">Start planning your trip activities!</p>
                <Button
                  onClick={() => handleQuickAction({ tab: 'activities' })}
                  size="sm"
                  className="bg-gradient-to-r from-purple-500 to-pink-600 text-white"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Activity
                </Button>
              </div>
            )}
          </div>

          {/* Recent Expenses */}
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <CurrencyRupeeIcon className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Recent Expenses</h3>
              </div>
              {dashboardData.recentExpenses.length > 0 && (
                <button
                  onClick={() => handleViewAll('expenses')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all ({dashboardData.recentExpenses.length})
                </button>
              )}
            </div>
            
            {dashboardData.recentExpenses.length > 0 ? (
              <div className="overflow-hidden">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-600 uppercase">Expense</th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-600 uppercase hidden sm:table-cell">Category</th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recentExpenses.slice(0, 5).map((expense, index) => (
                      <tr key={expense._id || index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{expense.title}</p>
                            <div className="flex items-center mt-1">
                              <div className="h-6 w-6 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                                {expense.paidBy?.name?.charAt(0) || 'U'}
                              </div>
                              <p className="text-xs text-gray-500">{expense.paidBy?.name || 'Unknown'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 hidden sm:table-cell">
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full capitalize">
                            {expense.category || 'general'}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="font-semibold text-gray-900">₹{expense.amount || 0}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <CurrencyRupeeIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-700 mb-1">No expenses yet</h4>
                <p className="text-gray-500 text-sm mb-4">Start tracking your trip expenses!</p>
                <Button
                  onClick={() => handleQuickAction({ tab: 'expenses' })}
                  size="sm"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Expense
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Members & Stats */}
        <div className="space-y-6">
          {/* Members Section - FIXED: Now uses correct ID extraction */}
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-orange-600" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Travel Companions</h3>
                  <p className="text-sm text-gray-500">
                    {dashboardData.membersCount} travel companion{dashboardData.membersCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <span className="text-sm font-semibold text-blue-600">
                {dashboardData.membersCount} total
              </span>
            </div>
            
            <div className="space-y-3">
              {dashboardData.members.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.members.map((member, index) => {
                    // FIXED: Get user ID from multiple possible fields
                    const memberId = member.id || member._id || member.userId || user?._id;
                    const memberName = member.name || `Member ${index + 1}`;
                    const initial = memberName.charAt(0).toUpperCase();
                    
                    console.log('👤 Member data:', { index, memberName, memberId, member });
                    
                    // Skip rendering if no valid ID
                    if (!memberId) {
                      console.error('❌ Member has no valid ID:', member);
                      return (
                        <div key={`member-${index}`} className="flex items-center p-4 border border-gray-100 rounded-xl">
                          <div className="w-12 h-12 bg-gray-200 rounded-full mr-3 flex items-center justify-center text-gray-500">
                            {initial}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800">{memberName}</h4>
                            <p className="text-xs text-red-500">Profile unavailable</p>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <button
                        key={memberId}
                        onClick={() => {
                          console.log('🎯 View Profile clicked:', { memberId, memberName });
                          handleViewProfile(memberId);
                        }}
                        className="flex items-center p-4 w-full border border-gray-100 rounded-xl hover:shadow-md hover:bg-gray-50 transition-all duration-200 text-left group active:scale-[0.98] transition-transform"
                      >
                        <div className="relative mr-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {initial}
                          </div>
                          {member.role === 'creator' && (
                            <div className="absolute -top-1 -right-1 bg-yellow-500 text-white p-1 rounded-full">
                              <FireIcon className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-800">
                              {memberName}
                              {user && memberId === user._id && (
                                <span className="ml-2 text-xs text-blue-600 font-normal">(You)</span>
                              )}
                            </h4>
                            {member.role === 'creator' && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Creator</span>
                            )}
                            <ArrowRightIcon className="h-3 w-3 text-gray-400 group-hover:text-blue-500 ml-auto" />
                          </div>
                          
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`text-xs ${
                              member.role === 'creator' ? 'text-yellow-600' : 'text-gray-600'
                            }`}>
                              {member.role === 'creator' ? 'Group Creator' : 'Member'}
                            </span>
                            
                            {member.email && (
                              <span className="text-xs text-gray-500 truncate max-w-[120px]">
                                {member.email}
                              </span>
                            )}
                            
                            {member.isActive && (
                              <div className="flex items-center gap-1 ml-auto">
                                <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                                <span className="text-xs text-gray-500">Active</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-700 mb-1">No members yet</h4>
                  <p className="text-gray-500 text-sm mb-4">
                    {membership?.isCreator ? 'Invite friends to join your trip!' : 'Only you in this group'}
                  </p>
                </div>
              )}
              
              {dashboardData.members.length > 0 && (
                <Button
                  onClick={() => handleViewAll('members')}
                  className="w-full bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 mt-4"
                  size="sm"
                >
                  View All Members
                </Button>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-lg p-5 md:p-6 text-white">
            <h3 className="text-lg font-bold mb-5">Trip Overview</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <MapPinIcon className="h-5 w-5 text-blue-300" />
                  </div>
                  <span className="text-sm">Route Plans</span>
                </div>
                <span className="font-semibold">{dashboardData.totalRoutePlans}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-300" />
                  </div>
                  <span className="text-sm">Active Polls</span>
                </div>
                <span className="font-semibold">{dashboardData.totalPolls}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <CheckCircleIcon className="h-5 w-5 text-purple-300" />
                  </div>
                  <span className="text-sm">Checklists</span>
                </div>
                <span className="font-semibold">{dashboardData.totalChecklists}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-500/20 rounded-lg">
                    <HomeIcon className="h-5 w-5 text-indigo-300" />
                  </div>
                  <span className="text-sm">Accommodations</span>
                </div>
                <span className="font-semibold">{dashboardData.totalAccommodations}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <TruckIcon className="h-5 w-5 text-red-300" />
                  </div>
                  <span className="text-sm">Transport Bookings</span>
                </div>
                <span className="font-semibold">{dashboardData.totalTransportations}</span>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-white/10">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-300">
                <GlobeAltIcon className="h-4 w-4" />
                <span>Everything ready for your journey!</span>
              </div>
            </div>
          </div>

          {/* Top Route Plan */}
          {dashboardData.topRoutePlan && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <FireIcon className="h-5 w-5 text-purple-600" />
                <h4 className="font-bold text-gray-900">Most Popular Route</h4>
              </div>
              <div className="space-y-2">
                <h5 className="font-semibold text-gray-800">{dashboardData.topRoutePlan.title}</h5>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {dashboardData.topRoutePlan.description}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-1">
                    <MapPinIcon className="h-4 w-4 text-gray-500" />
                    <span className="text-xs text-gray-600">
                      {dashboardData.topRoutePlan.waypoints?.length || 0} stops
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <UsersIcon className="h-4 w-4 text-gray-500" />
                    <span className="text-xs font-semibold text-purple-600">
                      {dashboardData.topRoutePlan.totalVotes} votes
                    </span>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => handleViewAll('routes')}
                className="w-full mt-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white"
                size="sm"
              >
                View All Routes
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation Helper */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <button
          onClick={handleRefresh}
          className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow"
        >
          <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default DashboardSummary;